import React, { useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ChannelViewChannelMessageFragment,
  ReactionFragment,
  useUpdateChannelMessageAttributesMutation,
} from 'shared/types/graphql';
import { BaseEmoji, Picker } from 'emoji-mart';
import { Box, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import * as Sentry from '@sentry/react';
import useIsMobile from 'shared/hooks/useIsMobile';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

type IMessageReactionsProps = {
  message: ChannelViewChannelMessageFragment;
  showQuickReactions?: boolean;
  showPlacedReactions?: boolean;
};

const quickReactions = ['👍', '👎', '👀'];

const MessageReactions = ({
  message,
  showQuickReactions,
  showPlacedReactions,
}: IMessageReactionsProps): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const { isMobile } = useIsMobile();
  const userId = clinicUser?.id;
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = React.useState(false);
  const [updateChannelMessage] = useUpdateChannelMessageAttributesMutation();
  const toast = useToast();
  const reactions = useMemo(() => message.reactions.filter((reaction) => !reaction.deletedAt), [message.reactions]);
  const { trackEvent } = useTracking();

  const userReactions = useMemo(() => {
    const userReactions: { reactions: ReactionFragment[]; array: string[] } = { reactions: [], array: [] };
    userReactions.reactions = reactions.filter((reaction) => reaction.userId === userId);
    userReactions.array = reactions
      .filter((reaction) => reaction.userId === userId)
      .map((reaction) => reaction.reaction);
    return userReactions;
  }, [reactions, userId]);

  const groupedReactions = useMemo(() => {
    const groupedReactions: { [key: string]: { times: number; users: string[] } } = {};
    for (const reaction of reactions) {
      const { nameDisplay, namePrefix, firstName, lastName, nameSuffix } = reaction.user;
      const userName = (
        nameDisplay || `${namePrefix || ''} ${firstName || ''} ${lastName || ''} ${nameSuffix || ''}`
      ).trim();
      if (!groupedReactions[reaction.reaction]) {
        groupedReactions[reaction.reaction] = { times: 0, users: [] };
      }
      groupedReactions[reaction.reaction].times = (groupedReactions[reaction.reaction]?.times || 0) + 1;
      groupedReactions[reaction.reaction].users.push(userName);
    }
    return groupedReactions;
  }, [reactions]);

  const handleReactionClick = (reactionEmoji: string): void => {
    try {
      if (clinicUser?.id && reactionEmoji) {
        if (userReactions?.array.includes(reactionEmoji)) {
          const reactionId = reactions.find(
            (reaction) => reaction.reaction === reactionEmoji && reaction.userId === userId,
          )?.id;
          updateChannelMessage({
            variables: {
              where: { id: message.id },
              data: {
                reactions: {
                  update: [{ where: { id: reactionId }, data: { deletedAt: new Date().toISOString() } }],
                },
              },
            },
          });
        } else {
          updateChannelMessage({
            variables: {
              where: { id: message.id },
              data: {
                reactions: {
                  create: [
                    {
                      reaction: reactionEmoji,
                      user: { connect: { id: userId } },
                    },
                  ],
                },
              },
            },
          });
        }
      }
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'Whoops! An error occurred while reacting to the message. Please try again.',
      });
      console.error(e);
    }
  };
  return (
    <Flex flexDirection="column">
      <Flex>
        {showPlacedReactions && (
          <HStack spacing={1}>
            {Object.keys(groupedReactions).map((reaction, index) => {
              return (
                <Tooltip
                  key={message.id + 'reaction' + index}
                  label={groupedReactions[reaction].users.join(', ')}
                  placement="top"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(): void => {
                      trackEvent(ConversationEvents.ChannelQuickEmojiSelected);
                      handleReactionClick(reaction);
                    }}
                    isActive={userReactions?.array.includes(reaction)}
                  >
                    <Box>{reaction}</Box>
                    {groupedReactions[reaction] && groupedReactions[reaction].times > 1 && (
                      <Text as="p" size="xs" ml={1}>
                        {groupedReactions[reaction] && groupedReactions[reaction].times > 1
                          ? ` ${groupedReactions[reaction].times}`
                          : ''}
                      </Text>
                    )}
                  </Button>
                </Tooltip>
              );
            })}
          </HStack>
        )}

        {showQuickReactions && (
          <HStack spacing="1">
            {quickReactions.map((reactionEmoji, index) => {
              return (
                <Button
                  isActive={userReactions?.array.includes(reactionEmoji)}
                  variant="ghostNeutral"
                  size="sm"
                  aria-label="Quick reaction"
                  key={`message-quick-reaction-${message.id}-${index}`}
                  onClick={(): void => {
                    handleReactionClick(reactionEmoji);
                  }}
                >
                  {reactionEmoji}
                </Button>
              );
            })}
          </HStack>
        )}
        {!isMobile && (
          <Popover
            anchor={
              <Flex ml={1}>
                <Button
                  variant="ghostNeutral"
                  size="sm"
                  aria-label="Send email"
                  iconName="smileyFace"
                  onClick={(): void => {
                    setIsEmojiPickerOpen(!isEmojiPickerOpen);
                  }}
                />
              </Flex>
            }
            placement={showQuickReactions ? 'left' : 'right'}
            isLazy
            isOpen={isEmojiPickerOpen}
            onClose={(): void => {
              setIsEmojiPickerOpen(false);
            }}
            variant="default"
            size="xl"
            contentProps={{ w: 'auto' }}
          >
            <Box m={-2}>
              <Picker
                autoFocus
                native
                title="Pick your emoji…"
                emoji="point_up"
                style={{ zIndex: 999, border: 'none' }}
                onSelect={(baseEmoji: BaseEmoji): void => {
                  trackEvent(ConversationEvents.ChannelEmojiSelectedFromDrawer);
                  handleReactionClick(baseEmoji.native);
                  setIsEmojiPickerOpen(false);
                }}
              />
            </Box>
          </Popover>
        )}
      </Flex>
    </Flex>
  );
};

export default MessageReactions;
